import { ref, computed, watch } from 'vue'
import { setCanvasStyle as mapboxSetCanvasStyle } from '@utils/setCanvasStyle'
import { addDiamondImage } from '@utils/mapboxDiamondImage'
import { geometryToBounds, resolvePolygonGeometry } from '@utils/mapboxGeometry'
import { queryClickableFeatures, pickClickFeature } from '@utils/mapClickQuery'
import {
  HOVER_LAYER_IDS,
  INTERACTIVE_POLYGON_LAYER_IDS,
  MAP_ZOOM_DEFAULT,
  POLYGON_DIAMOND_COLOR,
  MAP_IMAGE_POLYGON_DIAMOND,
  MAP_POLYGON_DETAIL_ZOOM,
  MAP_POLYGON_DIAMOND_FADE_END
} from '@root/constants'

export function useGeoDataMapInteractions({ getPolygonFeatures, store }) {
  const map = ref(null)
  const popUpLngLat = ref(null)
  const iccaInfo = ref({})
  const currentZoom = ref(MAP_ZOOM_DEFAULT)
  const showPolygonDiamondLegend = computed(() => currentZoom.value < MAP_POLYGON_DETAIL_ZOOM)

  function onMouseenter() {
    mapboxSetCanvasStyle(map.value, { cursor: 'pointer' })
  }

  function onMouseleave() {
    mapboxSetCanvasStyle(map.value, { cursor: '' })
  }

  function registerHoverInteractions(mapInstance) {
    HOVER_LAYER_IDS.forEach((layerId) => {
      mapInstance.on('mouseenter', layerId, onMouseenter)
      mapInstance.on('mouseleave', layerId, onMouseleave)
    })
  }

  function closePopup() {
    popUpLngLat.value = null
    iccaInfo.value = {}
  }

  function openPopUp(lngLat) {
    popUpLngLat.value = lngLat
  }

  function activateFeature(feature, clickLngLat) {
    const { properties, geometry, layer } = feature
    const popupLngLat = geometry.type === 'Point' ? geometry.coordinates : [clickLngLat.lng, clickLngLat.lat]
    iccaInfo.value = properties

    const isPolygonAreaClick = INTERACTIVE_POLYGON_LAYER_IDS.includes(layer?.id)
    if (isPolygonAreaClick && map.value.getZoom() < MAP_POLYGON_DETAIL_ZOOM) {
      const polygonGeometry = resolvePolygonGeometry(
        properties,
        geometry,
        getPolygonFeatures()
      )
      if (polygonGeometry) {
        map.value.fitBounds(geometryToBounds(polygonGeometry), {
          padding: 48,
          maxZoom: MAP_POLYGON_DIAMOND_FADE_END,
          duration: 800
        })
        map.value.once('moveend', () => openPopUp(popupLngLat))
        return
      }
    }

    openPopUp(popupLngLat)
  }

  function onMapClick(e) {
    const feature = pickClickFeature(queryClickableFeatures(map.value, e.point))
    if (!feature) return

    activateFeature(feature, e.lngLat)
  }

  function onMapLoad(e) {
    map.value = e.map
    addDiamondImage(map.value, MAP_IMAGE_POLYGON_DIAMOND, POLYGON_DIAMOND_COLOR)
    map.value.setZoom(MAP_ZOOM_DEFAULT)
    currentZoom.value = map.value.getZoom()
    map.value.on('zoom', () => {
      currentZoom.value = map.value.getZoom()
    })
    registerHoverInteractions(map.value)
    map.value.on('click', onMapClick)
  }

  watch(() => store.getters['screenSize/isDesktop'], (screenIsDesktop) => {
    if (!map.value) return

    if (screenIsDesktop) {
      map.value.scrollZoom.enable()
    } else {
      map.value.scrollZoom.disable()
    }
  })

  return {
    popUpLngLat,
    iccaInfo,
    showPolygonDiamondLegend,
    onMapLoad,
    closePopup
  }
}
